import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, UploadCloud, LogOut, RefreshCw, Inbox, ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "etmb_admin_token";

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-[#4361C2] outline-none py-3 text-sm font-light placeholder:text-ash/60 transition-colors duration-300 rounded-none";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return e?.message || "Something went wrong";
};

const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess(data.email);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/15 p-8 md:p-10" data-testid="admin-login-form">
        <img src="/assets/etmb-logo.png" alt="ETMB" className="h-12 w-auto mb-8" />
        <h1 className="font-display text-2xl uppercase font-bold tracking-tight mb-1">Admin Access</h1>
        <p className="text-xs text-ash mb-8 tracking-wide">Private — enquiries &amp; project photos</p>
        <label className="text-[10px] tracking-[0.3em] uppercase text-ash">Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="info@etmbdesignbuild.com" className={`${inputCls} mb-6`} data-testid="admin-email-input" />
        <label className="text-[10px] tracking-[0.3em] uppercase text-ash">Password</label>
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={`${inputCls} mb-6`} data-testid="admin-password-input" />
        {error && <p className="text-red-400 text-xs mb-4" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} className="w-full border border-white py-4 text-xs tracking-[0.3em] uppercase hover:bg-white hover:text-black disabled:opacity-50 transition-colors duration-300" data-testid="admin-login-submit">
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const Enquiries = () => {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: authHeaders() });
      setItems(data);
    } catch (e) {
      toast.error(errText(e));
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, { headers: authHeaders() });
      setItems((l) => l.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (items === null) return <p className="text-ash text-sm" data-testid="enquiries-loading">Loading enquiries…</p>;
  if (!items.length) return <p className="text-ash text-sm" data-testid="enquiries-empty">No enquiries yet. They'll appear here as soon as someone submits the contact form.</p>;

  return (
    <div className="space-y-4" data-testid="enquiries-list">
      {items.map((q) => (
        <div key={q.id} className="border border-white/15 p-5 md:p-6" data-testid={`enquiry-item-${q.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-display text-lg uppercase font-bold tracking-tight">{q.name}</div>
              <a href={`mailto:${q.email}`} className="text-sm text-[#7B96E8]" data-testid={`enquiry-email-${q.id}`}>{q.email}</a>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[10px] tracking-[0.25em] uppercase text-ash border border-white/15 px-2 py-1">{q.project_type}</span>
              <span className="text-[10px] text-ash">{new Date(q.created_at).toLocaleString()}</span>
              <button onClick={() => remove(q.id)} className="text-ash hover:text-red-400 transition-colors" aria-label="Delete enquiry" data-testid={`enquiry-delete-${q.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
          <p className="mt-4 text-sm font-light text-paper/90 whitespace-pre-wrap">{q.message}</p>
        </div>
      ))}
    </div>
  );
};

const Photos = () => {
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ title: "", category: "project", project_type: "", location: "", year: "" });
  const [progress, setProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/photos`);
      setPhotos(data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setMeta((m) => ({ ...m, [k]: e.target.value }));

  const pick = (f) => {
    if (!f) return;
    if (!/\.(jpe?g|png|gif|webp)$/i.test(f.name)) return toast.error("Only JPG, PNG, GIF or WEBP images");
    if (f.size > 10 * 1024 * 1024) return toast.error("Max size is 10MB");
    setFile(f);
    if (!meta.title) setMeta((m) => ({ ...m, title: f.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ") }));
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a photo first");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
    setProgress(0);
    try {
      await axios.post(`${API}/photos`, fd, {
        headers: authHeaders(),
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || 1)) * 100)),
      });
      toast.success("Photo published to the website");
      setFile(null);
      setMeta({ title: "", category: meta.category, project_type: "", location: "", year: "" });
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setProgress(null);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/photos/${id}`, { headers: authHeaders() });
      setPhotos((l) => l.filter((x) => x.id !== id));
      toast.success("Photo removed from the website");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <form onSubmit={upload} className="lg:col-span-5 space-y-6" data-testid="photo-upload-form">
        <div
          className={`border-2 border-dashed ${dragOver ? "border-[#4361C2] bg-[#19254D]/20" : "border-white/20"} p-8 text-center cursor-pointer transition-colors`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files[0]); }}
          onClick={() => document.getElementById("photo-file-input").click()}
          data-testid="photo-drop-zone"
        >
          <UploadCloud size={28} className="mx-auto mb-3 text-ash" />
          {file ? (
            <p className="text-sm" data-testid="photo-selected-name">{file.name} <span className="text-ash">({(file.size / 1024 / 1024).toFixed(1)}MB)</span></p>
          ) : (
            <p className="text-sm text-ash">Drag a photo here or click to browse<br /><span className="text-[10px] tracking-widest uppercase">JPG / PNG / WEBP — max 10MB</span></p>
          )}
          <input id="photo-file-input" type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="photo-file-input" />
        </div>

        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-ash">Shows on site as</label>
          <select value={meta.category} onChange={set("category")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="photo-category-select">
            <option value="project" className="bg-[#0A0A0A]">Project card — "Selected Work" grid</option>
            <option value="gallery" className="bg-[#0A0A0A]">Gallery photo — "From the Field" section</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-ash">Title</label>
          <input required value={meta.title} onChange={set("title")} placeholder="e.g. Riley Residence" className={inputCls} data-testid="photo-title-input" />
        </div>
        {meta.category === "project" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-ash">Type</label>
              <input value={meta.project_type} onChange={set("project_type")} placeholder="Addition" className={inputCls} data-testid="photo-type-input" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-ash">Location</label>
              <input value={meta.location} onChange={set("location")} placeholder="Bethesda, MD" className={inputCls} data-testid="photo-location-input" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-ash">Year</label>
              <input value={meta.year} onChange={set("year")} placeholder="2026" className={inputCls} data-testid="photo-year-input" />
            </div>
          </div>
        )}
        <button type="submit" disabled={progress !== null} className="w-full border border-white py-4 text-xs tracking-[0.3em] uppercase hover:bg-white hover:text-black disabled:opacity-50 transition-colors duration-300" data-testid="photo-upload-submit">
          {progress !== null ? `Uploading… ${progress}%` : "Publish to Website"}
        </button>
      </form>

      <div className="lg:col-span-7">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ash mb-4">Live on the website ({photos.length})</div>
        {!photos.length ? (
          <p className="text-ash text-sm" data-testid="photos-empty">No uploaded photos yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="photos-grid">
            {photos.map((p) => (
              <div key={p.id} className="relative group border border-white/15" data-testid={`photo-item-${p.id}`}>
                <img src={`${API}/photos/file/${p.id}`} alt={p.title} loading="lazy" className="w-full aspect-square object-cover" />
                <div className="absolute inset-0 bg-black/70 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                  <span className="text-xs text-center">{p.title}</span>
                  <span className="text-[9px] tracking-[0.25em] uppercase text-ash">{p.category === "project" ? "Project card" : "Gallery"}</span>
                  <button onClick={() => remove(p.id)} className="mt-1 text-red-400 hover:text-red-300" aria-label="Delete photo" data-testid={`photo-delete-${p.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [email, setEmail] = useState("");
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return setAuthed(false);
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { setEmail(data.email); setAuthed(true); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="min-h-screen bg-[#050505] text-paper flex items-center justify-center text-ash text-sm">Checking session…</div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-paper" data-testid="admin-page">
      {!authed ? (
        <Login onSuccess={(em) => { setEmail(em); setAuthed(true); }} />
      ) : (
        <div className="px-6 md:px-12 py-10 max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/15 pb-6 mb-10">
            <div className="flex items-center gap-4">
              <img src="/assets/etmb-logo.png" alt="ETMB" className="h-10 w-auto" />
              <div>
                <h1 className="font-display text-xl uppercase font-bold tracking-tight" data-testid="admin-dashboard-title">Admin Dashboard</h1>
                <p className="text-[10px] tracking-[0.25em] uppercase text-ash">{email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a href="/" className="text-xs tracking-[0.25em] uppercase text-ash hover:text-paper transition-colors" data-testid="admin-view-site-link">View Site</a>
              <button onClick={logout} className="flex items-center gap-2 border border-white/30 px-4 py-2 text-xs tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-colors" data-testid="admin-logout-button">
                <LogOut size={13} /> Logout
              </button>
            </div>
          </div>

          <div className="flex gap-2 mb-10">
            <button onClick={() => setTab("enquiries")} className={`flex items-center gap-2 px-5 py-3 text-xs tracking-[0.25em] uppercase border transition-colors ${tab === "enquiries" ? "bg-white text-black border-white" : "border-white/20 text-ash hover:text-paper"}`} data-testid="admin-tab-enquiries">
              <Inbox size={14} /> Enquiries
            </button>
            <button onClick={() => setTab("photos")} className={`flex items-center gap-2 px-5 py-3 text-xs tracking-[0.25em] uppercase border transition-colors ${tab === "photos" ? "bg-white text-black border-white" : "border-white/20 text-ash hover:text-paper"}`} data-testid="admin-tab-photos">
              <ImageIcon size={14} /> Photo Drop Zone
            </button>
          </div>

          {tab === "enquiries" ? <Enquiries /> : <Photos />}
        </div>
      )}
    </div>
  );
}
